import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { platformLogout } from '../services/auth';
import { handoffToPlatform } from '../lib/platformAuth';
import { qk } from '../services/queryKeys';
import {
  fetchEmployees, fetchMyEmployee, fetchDepartments, fetchLeaveRequests, fetchWorkflowInstances,
  fetchExpenses, fetchAllExpenses, addEmployeeApi,
  fetchNotifications, markNotificationRead, markAllNotificationsRead,
  fetchAttendance, clockPunchApi,
  resolveAvatarSrc, claimIdentityApi,
} from '../services/api';
import type { Employee, InternalNotification } from '../types';
import { LIGHT_THEME_CSS, DARK_THEME_CSS, BASE_CSS } from '../theme';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import ToastHost from '../components/Toast';
import Modal from '../components/Modal';
import Button from '../components/Button';
import DatePicker from '../components/DatePicker';
import ClockCard from '../components/ClockCard';
import { useLiveEvents } from '../lib/eventStream';

import type { Role, EmploymentType } from '../types';

// ── Path constants ────────────────────────────────────────────────────────────
const SCREEN_PATH: Record<string, string> = {
  home: '/', profile: '/profile', directory: '/directory', myDepartment: '/my-department',
  leave: '/leave', attendance: '/attendance', payslips: '/payroll',
  documents: '/documents', workflows: '/workflows', expenses: '/expenses',
  performance: '/performance', settings: '/settings',
};

// ── Nav definitions per role ──────────────────────────────────────────────────
type NavEntry = { path: string; label: string; icon: string; badge?: string | null };

function buildNav(role: Role, pendingLeave: number, runningWf: number): NavEntry[] {
  if (role === 'ess') return [
    { path: '/',             label: 'Home',        icon: 'home' },
    { path: '/profile',      label: 'My profile',  icon: 'user' },
    { path: '/my-department', label: 'My department', icon: 'users' },
    { path: '/leave',        label: 'Leave',       icon: 'calendar' },
    { path: '/attendance',   label: 'Attendance',  icon: 'clock' },
    { path: '/payroll',      label: 'Payslips',    icon: 'wallet' },
    { path: '/documents',    label: 'Documents',   icon: 'doc' },
    { path: '/expenses',     label: 'Expenses',    icon: 'receipt' },
    { path: '/performance',  label: 'Performance', icon: 'star' },
  ];
  if (role === 'mss') return [
    { path: '/',             label: 'Home',        icon: 'home' },
    { path: '/profile',      label: 'My profile',  icon: 'user' },
    { path: '/directory',    label: 'My team',     icon: 'users' },
    { path: '/my-department', label: 'My department', icon: 'users' },
    { path: '/leave',        label: 'Approvals',   icon: 'calendar', badge: pendingLeave > 0 ? String(pendingLeave) : null },
    { path: '/attendance',   label: 'Attendance',  icon: 'clock' },
    { path: '/payroll',      label: 'Payslips',    icon: 'wallet' },
    { path: '/documents',    label: 'Documents',   icon: 'doc' },
    { path: '/expenses',     label: 'Expenses',    icon: 'receipt' },
    { path: '/performance',  label: 'Performance', icon: 'star' },
  ];
  return [
    { path: '/',             label: 'Home',        icon: 'home' },
    { path: '/profile',      label: 'My profile',  icon: 'user' },
    { path: '/directory',    label: 'Directory',   icon: 'users' },
    { path: '/my-department', label: 'My department', icon: 'users' },
    { path: '/leave',        label: 'Leave',       icon: 'calendar', badge: pendingLeave > 0 ? String(pendingLeave) : null },
    { path: '/attendance',   label: 'Attendance',  icon: 'clock' },
    { path: '/payroll',      label: 'Payroll',     icon: 'wallet' },
    { path: '/documents',    label: 'Documents',   icon: 'doc' },
    { path: '/workflows',    label: 'Workflows',   icon: 'flow', badge: runningWf > 0 ? String(runningWf) : null },
    { path: '/expenses',     label: 'Expenses',    icon: 'receipt' },
    { path: '/performance',  label: 'Performance', icon: 'star' },
    { path: '/settings',     label: 'Settings',    icon: 'settings' },
  ];
}

// ── Title derived from current URL path ───────────────────────────────────────
function screenTitle(pathname: string, role: Role): string {
  if (pathname === '/')                        return 'Home';
  if (pathname.startsWith('/profile'))         return 'Employee profile';
  if (pathname.startsWith('/directory'))       return role === 'mss' ? 'My team' : 'Directory';
  if (pathname.startsWith('/my-department'))   return 'My department';
  if (pathname.startsWith('/leave'))           return role === 'ess' ? 'Leave' : 'Leave & approvals';
  if (pathname.startsWith('/attendance'))      return 'Attendance';
  if (pathname.startsWith('/payroll'))         return role === 'hr' ? 'Payroll' : 'Payslips';
  if (pathname.startsWith('/documents'))       return 'Documents';
  if (pathname.startsWith('/workflows'))       return 'Lifecycle workflows';
  if (pathname.startsWith('/expenses'))        return 'Expenses';
  if (pathname.startsWith('/performance'))     return 'Performance';
  if (pathname.startsWith('/settings'))        return 'Settings';
  return 'SK People';
}

const ROLE_BREADCRUMB: Record<Role, string> = {
  ess: 'Employee self-service',
  mss: 'Manager self-service',
  hr:  'HR Admin',
};

const ROLE_LABELS: Record<Role, string> = {
  ess: 'Employee',
  mss: 'Manager',
  hr:  'HR Admin',
};

const menuItemStyle: React.CSSProperties = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 14px', background: 'transparent', border: 'none',
  cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', textAlign: 'left',
};


function injectCSS() {
  if (typeof document === 'undefined') return;
  const id = 'sk-theme-css';
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id;
  el.textContent = LIGHT_THEME_CSS + DARK_THEME_CSS + BASE_CSS;
  document.head.appendChild(el);
}

export default function WebShell() {
  const {
    role, theme, toggleTheme,
    profileId, employeeId, setEmployeeId, copilotOpen, toggleCopilot, modal, closeModal,
    logout, displayName, email, orgName, toast,
  } = useAppStore();

  const navigate = useNavigate();
  const location = useLocation();

  const { data: employees = [] } = useQuery({ queryKey: qk.employees(), queryFn: fetchEmployees });
  const { data: leaveRequests = [] } = useQuery({ queryKey: qk.leaveRequests(), queryFn: () => fetchLeaveRequests() });
  const { data: workflowInstances = [] } = useQuery({ queryKey: qk.workflowInstances(), queryFn: () => fetchWorkflowInstances() });
  const queryClient = useQueryClient();
  const { data: rawNotifications = [] } = useQuery({
    queryKey: qk.notifications(),
    queryFn: fetchNotifications,
    enabled: !!employeeId,
    // Live pushes over SSE (useLiveEvents below) do the real-time work now;
    // this is just a slow safety net in case a connection is silently missed.
    refetchInterval: 60_000,
  });
  useLiveEvents(queryClient, !!employeeId);
  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.notifications() }),
  });
  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.notifications() });
      setNotifOpen(false);
    },
  });

  // Resolve employee DB UUID once on mount (platform user ID → DB UUID)
  const { data: myEmployee, isError: myEmployeeError } = useQuery({
    queryKey: ['employees', 'me'],
    queryFn: fetchMyEmployee,
    enabled: !employeeId,
    retry: 1,
  });
  useEffect(() => {
    if (myEmployee?.id && myEmployee.id !== employeeId) setEmployeeId(myEmployee.id);
  }, [myEmployee?.id]);

  /*
   * Self-serve link (CC-001 CH-1.2). GET /employees/me no longer creates a
   * record for an unlinked login, and it no longer claims one by matching the
   * JWT's email — an email is re-assignable and often shared, so matching on it
   * let the wrong person inherit an employee record and its payslips. Linking is
   * now an explicit action matched on the VERIFIED phone in the token, which is
   * why this is a button rather than something that happens invisibly on load.
   */
  const claimMutation = useMutation({
    mutationFn: claimIdentityApi,
    onSuccess: (emp) => {
      setEmployeeId(emp.id);
      queryClient.invalidateQueries({ queryKey: ['employees', 'me'] });
      toast('Account linked');
    },
    onError: (e: Error) => toast(e.message || 'Could not link your account', 'danger'),
  });

  // me = the logged-in user's own employee record
  const me = myEmployee
    ?? employees.find((e) => e.email === email)
    ?? employees.find((e) => e.platformUserId === profileId);
  const [notifOpen, setNotifOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Check-in/check-out — sidebar quick clock, visible to every role
  const { data: myAttendance = [] } = useQuery({
    queryKey: qk.attendance(employeeId ?? ''),
    queryFn: () => fetchAttendance(employeeId!),
    enabled: !!employeeId,
  });
  const lastPunch = [...myAttendance].sort((a, b) => b.punchedAt.localeCompare(a.punchedAt))[0];
  const isClockedIn = lastPunch?.punchType === 'clock_in';
  const clockMutation = useMutation({
    mutationFn: (punchType: 'clock_in' | 'clock_out') => clockPunchApi(employeeId!, punchType),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.attendance(employeeId ?? '') }),
    onError: () => toast('Clock punch failed', 'danger'),
  });

  useEffect(() => { injectCSS(); }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-notif-panel]')) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  useEffect(() => {
    if (!accountOpen) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-account-panel]')) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [accountOpen]);

  // Badge counts (sidebar nav — still derived from live query data)
  const pendingLeave = leaveRequests.filter((r) => r.status === 'submitted').length;
  const runningWf = workflowInstances.filter((i) => i.state !== 'completed').length;

  // Bell notifications — from people_notifications table via API
  const NOTIF_ICON: Record<InternalNotification['type'], string> = {
    expense_submitted: 'receipt', expense_decided: 'receipt',
    leave_submitted: 'calendar', leave_decided: 'calendar',
  };
  const NOTIF_SCREEN: Record<InternalNotification['type'], string> = {
    expense_submitted: '/expenses', expense_decided: '/expenses',
    leave_submitted: '/leave', leave_decided: '/leave',
  };
  const unreadNotifications = rawNotifications.filter((n) => !n.readAt);
  const notifications = rawNotifications.map((n) => ({
    id: n.id,
    icon: NOTIF_ICON[n.type],
    screen: NOTIF_SCREEN[n.type],
    title: n.title,
    sub: n.body ?? '',
    time: n.createdAt.slice(0, 10),
    unread: !n.readAt,
  }));

  const navItems = buildNav(role, pendingLeave, runningWf);
  const title = screenTitle(location.pathname, role);
  const breadcrumb = ROLE_BREADCRUMB[role];

  /*
   * A MISSING EMPLOYEE RECORD MUST NOT LOCK AN HR ADMIN OUT OF THE CONSOLE.
   *
   * CH-1.2 stopped GET /employees/me from auto-seeding a record for whoever
   * asked, which was right — it matched on email, and an email is re-assignable
   * and often shared. But on a brand-new org NOBODY has an employee record yet,
   * the first admin included. Gating the whole shell on having one meant that
   * admin saw "Profile not set up yet" and a Sign out button, with no way to
   * reach Directory and add the first employee — themselves included. A closed
   * loop: the only person who can create employee records could not get to the
   * screen that creates them.
   *
   * Directory, Payroll, Settings and every other admin surface need no employee
   * record; only the self-service widgets do, and those already gate on
   * employeeId. So HR goes straight through with a banner saying why the
   * self-service parts are quiet. ESS and MSS still stop here, because until
   * they are linked there is genuinely nothing for them to see.
   */
  const unlinkedAdmin = Boolean(myEmployeeError) && role === 'hr';

  if (myEmployeeError && !unlinkedAdmin) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f5f7)', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>👤</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink, #111)', margin: '0 0 10px' }}>
            Profile not set up yet
          </h2>
          <p style={{ fontSize: 14, color: 'var(--ink-3, #888)', lineHeight: 1.7, margin: '0 0 20px' }}>
            Your HR admin hasn&apos;t created your employee profile yet, or it isn&apos;t linked to
            this login. If they&apos;ve just added you, link your account below.
          </p>
          {email && (
            <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border, #e0e0e0)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, textAlign: 'left' }}>
              {/*
                * Linking matches on the VERIFIED PHONE in the token, not on this
                * email, so the old "share your email and we'll match it" copy
                * promised something the API deliberately no longer does. The
                * email stays on screen because it is still how HR finds the
                * person; the instruction is now about the phone number.
                */}
              <p style={{ fontSize: 12, color: 'var(--ink-3, #888)', margin: '0 0 6px', fontWeight: 500 }}>
                ASK HR TO ADD YOU, WITH YOUR MOBILE NUMBER ON THE RECORD
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <code style={{ flex: 1, fontSize: 14, color: 'var(--ink, #111)', wordBreak: 'break-all' }}>{email}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(email)}
                  style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 6, background: 'var(--accent, #4F46E5)', color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                >
                  Copy
                </button>
              </div>
            </div>
          )}
          <button
            onClick={() => claimMutation.mutate()}
            disabled={claimMutation.isPending}
            style={{ padding: '10px 24px', borderRadius: 8, background: 'var(--accent, #4F46E5)', border: 'none', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginRight: 10, opacity: claimMutation.isPending ? 0.6 : 1 }}
          >
            {claimMutation.isPending ? 'Linking…' : 'Link my account'}
          </button>
          <button
            onClick={() => platformLogout().then(() => { logout(); handoffToPlatform(`${window.location.origin}/login`); })}
            style={{ padding: '10px 24px', borderRadius: 8, background: 'none', border: '1.5px solid var(--border, #e0e0e0)', color: 'var(--ink-2, #444)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Profile completion banner — shown to HR admins whose auto-seeded record has no job title yet.
  const showProfileBanner = role === 'hr' && me && !me.jobTitle;

  return (
    <div className="sk-shell" style={copilotOpen ? { paddingRight: 320 } : {}}>

      {/* ── Left navigation ── */}
      <nav className="sk-nav">

        {/* Brand */}
        <div style={{ padding: '16px 16px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--accent)', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            SK
          </div>
          <div style={{ lineHeight: 1.15, flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em', color: 'var(--ink)' }}>People</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{orgName}</div>
          </div>
          <button
            onClick={toggleTheme}
            title="Toggle theme"
            style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border, #e0e0e0)', background: 'var(--surface, #fff)', display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={14} color="var(--ink-3)" />
          </button>
        </div>

        {/* Role badge — read-only, derived from JWT */}
        <div style={{ padding: '0 12px 12px' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4, #bbb)', margin: '0 4px 6px', fontWeight: 600 }}>
            Role
          </div>
          <div style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--paper-3,#f3f4f6)', fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--ink-2)' }}>
            {ROLE_LABELS[role]}
          </div>
        </div>

        {/* Check-in/check-out — sidebar quick clock */}
        <ClockCard
          variant="compact"
          firstName={me?.firstName || displayName || 'there'}
          lastName={me?.lastName}
          avatarUrl={resolveAvatarSrc(me?.avatarUrl)}
          avatarColor={me?.avatarColor}
          isClockedIn={isClockedIn}
          since={isClockedIn ? lastPunch?.punchedAt : undefined}
          disabled={!employeeId}
          pending={clockMutation.isPending}
          onToggle={() => clockMutation.mutate(isClockedIn ? 'clock_out' : 'clock_in')}
        />

        {/* Nav items */}
        <div className="sk-nav-body">
          {navItems.map((item) => {
            const isProfile = item.path === '/profile';
            const isActive = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            return (
              <div
                key={item.path + item.label}
                className={`sk-nav-item${isActive ? ' active' : ''}`}
                onClick={() => navigate(isProfile && me?.id ? `/profile/${me.id}` : item.path)}
              >
                <Icon name={item.icon} size={16} color="currentColor" />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.badge && (
                  <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>
                    {item.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer — account owner */}
        <div style={{ position: 'relative' }} data-account-panel>
          {accountOpen && (
            <div style={{ position: 'absolute', bottom: '100%', left: 8, right: 8, marginBottom: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 -4px 24px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink)' }}>{displayName || 'Account'}</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>{email}</div>
              </div>
              <div style={{ padding: '6px 0' }}>
                <button
                  onClick={() => { navigate('/settings'); setAccountOpen(false); }}
                  style={menuItemStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Icon name="settings" size={14} color="var(--ink-2)" />
                  <span>Settings</span>
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', gap: 10 }}>
                  <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={14} color="var(--ink-2)" />
                  <span style={{ flex: 1, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
                    {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </span>
                  <button
                    onClick={toggleTheme}
                    style={{
                      width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0,
                      background: theme === 'dark' ? 'var(--accent)' : 'var(--border)',
                      transition: 'background 0.2s',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3, width: 14, height: 14, borderRadius: '50%', background: '#fff',
                      left: theme === 'dark' ? 19 : 3,
                      transition: 'left 0.2s',
                    }} />
                  </button>
                </div>
                <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
                <button
                  onClick={() => { setAccountOpen(false); platformLogout().then(() => { logout(); handoffToPlatform(`${window.location.origin}/login`); }); }}
                  style={{ ...menuItemStyle, color: '#dc2626' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Icon name="logout" size={14} color="#dc2626" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}

          <button
            onClick={() => setAccountOpen((v) => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: accountOpen ? 'var(--bg)' : 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 12, fontWeight: 700 }}>
              {(displayName || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName || 'Account'}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {email}
              </div>
            </div>
            <span style={{ display: 'flex', transform: accountOpen ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }}>
              <Icon name="chevron_down" size={12} color="var(--ink-3)" />
            </span>
          </button>
        </div>
      </nav>

      {/* ── Main content ── */}
      <div className="sk-main">
        <header className="sk-topbar">
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{breadcrumb}</div>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.1, color: 'var(--ink)' }}>
              {title}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ position: 'relative', width: 240 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4,#bbb)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input className="t-input" placeholder="Search people, docs…" style={{ paddingLeft: 30, height: 32, borderRadius: 999, fontSize: 'var(--fs-sm)' }} />
          </div>
          <div style={{ position: 'relative' }} data-notif-panel>
            <button
              onClick={() => setNotifOpen((v) => !v)}
              style={{ width: 32, height: 32, borderRadius: 8, background: notifOpen ? 'var(--accent-soft,#eef2ff)' : 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
              title="Notifications"
            >
              <Icon name="bell" size={15} color={notifOpen ? 'var(--accent)' : 'var(--ink-2)'} />
              {unreadNotifications.length > 0 && (
                <span style={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />
              )}
            </button>
            {notifOpen && (
              <div style={{ position: 'absolute', top: 38, right: 0, width: 320, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontWeight: 600, fontSize: 'var(--fs-base)', color: 'var(--ink)' }}>Notifications</span>
                  {unreadNotifications.length > 0 && (
                    <button
                      onClick={() => markAllReadMutation.mutate()}
                      style={{ fontSize: 'var(--fs-xs)', color: 'var(--accent)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
                    You're all caught up
                  </div>
                ) : (
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {notifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => {
                          if (n.unread) markReadMutation.mutate(n.id);
                          navigate(n.screen);
                          setNotifOpen(false);
                        }}
                        style={{ display: 'flex', gap: 12, padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.15s', background: n.unread ? 'var(--accent-soft,#eef2ff)' : 'transparent' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = n.unread ? 'var(--accent-soft,#eef2ff)' : 'transparent')}
                      >
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-soft,#eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon name={n.icon} size={14} color="var(--accent)" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 'var(--fs-sm)', fontWeight: n.unread ? 600 : 500, color: 'var(--ink)', marginBottom: 2 }}>{n.title}</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.sub}</div>
                        </div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-4,#bbb)', flexShrink: 0 }}>{n.time}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={toggleCopilot}
            style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, background: copilotOpen ? 'var(--accent-soft,#eef2ff)' : 'none', border: '1px solid var(--border,#e0e0e0)', cursor: 'pointer', fontSize: 'var(--fs-sm)', fontWeight: 500, color: copilotOpen ? 'var(--accent)' : 'var(--ink-2)' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10z" />
            </svg>
            Knack AI
          </button>
        </header>

        <main className="sk-content">
          {unlinkedAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
              <span style={{ fontSize: 18 }}>👋</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e40af' }}>You&apos;re not on the employee list yet — </span>
                <span style={{ fontSize: 14, color: '#3b82f6' }}>
                  admin tools all work. Add yourself and your team in Directory to switch on
                  attendance, leave and payslips for your own account.
                </span>
              </div>
              <button
                onClick={() => navigate('/directory')}
                style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 6, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}
              >
                Open Directory
              </button>
            </div>
          )}
          {showProfileBanner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
              <span style={{ fontSize: 18 }}>✏️</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e40af' }}>Complete your profile — </span>
                <span style={{ fontSize: 14, color: '#3b82f6' }}>Add your job title and phone number so the team can find you.</span>
              </div>
              <button
                onClick={() => navigate(`/profile/${me!.id}`)}
                style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 6, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}
              >
                Update profile
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* ── Copilot panel ── */}
      {copilotOpen && (
        <div className="sk-copilot">
          <div className="sk-copilot-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--accent-soft,#eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10z" />
                </svg>
              </div>
              <div style={{ lineHeight: 1.1 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--fs-base)' }}>Knack AI</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Grounded in your tenant's policies</div>
              </div>
            </div>
            <button onClick={toggleCopilot} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <Icon name="x" size={14} color="var(--ink-3)" />
            </button>
          </div>
          <div className="sk-copilot-body">
            <div className="sk-copilot-msg assistant">
              Hi {me?.firstName}! I'm Knack AI. Ask me anything about HR policies, leave balances, or how to use SK People.
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', textAlign: 'center', marginTop: 8 }}>
              Full AI integration available in Phase D
            </div>
          </div>
          <div className="sk-copilot-foot">
            <input className="t-input" placeholder="Ask Knack AI…" style={{ flex: 1 }} />
            <button style={{ width: 32, height: 34, borderRadius: 8, background: 'var(--accent)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chevron_right" size={14} color="white" />
            </button>
          </div>
        </div>
      )}

      {modal.type === 'add_employee' && <AddEmployeeModal />}
      {modal.type === 'invite_member' && <InviteMemberModal />}
      <ToastHost />
    </div>
  );
}

function AddEmployeeModal() {
  const { closeModal, toast, email: myEmail, profileId } = useAppStore();
  const queryClient = useQueryClient();
  const { data: departments = [] } = useQuery({ queryKey: qk.departments(), queryFn: fetchDepartments });

  const [state, setState] = useState<{
    firstName: string; lastName: string; email: string; jobTitle: string;
    departmentId: string; employmentType: EmploymentType; joinDate: string;
  }>({
    firstName: '', lastName: '', email: '', jobTitle: '',
    departmentId: '', employmentType: 'full_time', joinDate: '',
  });

  // Set the default department once the query resolves
  useEffect(() => {
    if (departments.length > 0 && !state.departmentId) {
      setState((s) => ({ ...s, departmentId: departments[0].id }));
    }
  }, [departments]);

  const addMutation = useMutation({
    mutationFn: addEmployeeApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.employees() });
      toast('Employee added successfully');
      closeModal();
    },
    onError: () => toast('Failed to add employee', 'danger'),
  });

  return (
    <Modal
      title="Add employee"
      onClose={closeModal}
      size="wide"
      footer={
        <>
          <Button variant="ghost" onClick={closeModal}>Cancel</Button>
          <Button variant="accent" disabled={addMutation.isPending} onClick={() => addMutation.mutate({
            firstName: state.firstName,
            lastName: state.lastName,
            personalEmail: state.email,
            employmentType: state.employmentType,
            joinDate: state.joinDate,
            // Auto-link platform account when the email matches the logged-in user
            ...(myEmail && state.email.toLowerCase() === myEmail.toLowerCase() ? { platformUserId: profileId } : {}),
          } as Partial<Employee>)}>Add employee</Button>
        </>
      }
    >
      <div className="form-grid">
        <div className="form-row">
          <label>First name</label>
          <input className="t-input" value={state.firstName} onChange={(e) => setState(s => ({ ...s, firstName: e.target.value }))} />
        </div>
        <div className="form-row">
          <label>Last name</label>
          <input className="t-input" value={state.lastName} onChange={(e) => setState(s => ({ ...s, lastName: e.target.value }))} />
        </div>
      </div>
      <div className="form-row">
        <label>Join date</label>
        <DatePicker value={state.joinDate} onChange={(v) => setState(s => ({ ...s, joinDate: v }))} />
      </div>
      <div className="form-row">
        <label>Work email</label>
        <input type="email" className="t-input" value={state.email} onChange={(e) => setState(s => ({ ...s, email: e.target.value }))} />
      </div>
      <div className="form-row">
        <label>Job title</label>
        <input className="t-input" value={state.jobTitle} onChange={(e) => setState(s => ({ ...s, jobTitle: e.target.value }))} />
      </div>
      <div className="form-grid">
        <div className="form-row">
          <label>Department</label>
          <select className="t-input" value={state.departmentId} onChange={(e) => setState(s => ({ ...s, departmentId: e.target.value }))}>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label>Employment type</label>
          <select className="t-input" value={state.employmentType} onChange={(e) => setState(s => ({ ...s, employmentType: e.target.value as EmploymentType }))}>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="contract">Contract</option>
            <option value="intern">Intern</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}

function InviteMemberModal() {
  const { closeModal, toast, orgId, token } = useAppStore();
  const [form, setForm] = useState({ email: '', hrRole: 'ess' as 'ess' | 'mss' | 'hr' });
  const [loading, setLoading] = useState(false);

  // Map HR role → platform invitation role
  const platformRoleMap: Record<string, 'user' | 'admin' | 'org_admin'> = {
    ess: 'user',
    mss: 'admin',
    hr: 'org_admin',
  };

  const handleSend = async () => {
    if (!form.email.trim()) return;
    if (!orgId) { toast('Organisation ID not found — please log out and back in', 'danger'); return; }
    setLoading(true);
    try {
      const { sendInvitation } = await import('../services/auth');
      await sendInvitation(orgId, form.email.trim().toLowerCase(), platformRoleMap[form.hrRole], token ?? '');
      toast(`Invitation sent to ${form.email}`);
      closeModal();
    } catch {
      toast('Failed to send invitation — check the email and try again', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Invite member"
      onClose={closeModal}
      footer={
        <>
          <Button variant="ghost" onClick={closeModal}>Cancel</Button>
          <Button variant="accent" disabled={loading || !form.email.trim()} onClick={handleSend}>
            {loading ? 'Sending…' : 'Send invitation'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', margin: 0 }}>
          An email invitation will be sent. The person sets their own password when they accept.
        </p>
      </div>
      <div className="form-row">
        <label>Work email</label>
        <input
          type="email"
          className="t-input"
          placeholder="jane@company.com"
          value={form.email}
          onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
      </div>
      <div className="form-row" style={{ marginTop: 12 }}>
        <label>Role in SK People</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {([
            { value: 'ess', label: 'Employee', desc: 'View own data, apply leave, log expenses' },
            { value: 'mss', label: 'Manager', desc: 'Approve team leave, view direct reports' },
            { value: 'hr',  label: 'HR Admin', desc: 'Full access — all employees, payroll, settings' },
          ] as const).map(({ value, label, desc }) => (
            <button
              key={value}
              onClick={() => setForm(f => ({ ...f, hrRole: value }))}
              style={{
                flex: 1, padding: '10px 8px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                border: form.hrRole === value ? '2px solid var(--accent)' : '2px solid var(--border)',
                background: form.hrRole === value ? 'var(--accent-soft)' : 'var(--surface)',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', color: form.hrRole === value ? 'var(--accent)' : 'var(--ink)', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.4 }}>{desc}</div>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
